import React, { useEffect, useMemo, useState } from "react";
import { api } from "../utils/api";
import { showErrorToast, showSuccessToast } from "../utils/toast";
import { PLACEHOLDER_REGO } from "../utils/vehiclePlaceholders";

const MODE_CONFIG = {
  activation: {
    title: "Activate your memberships",
    description: (count) =>
      `You have ${count} vehicle${count !== 1 ? "s" : ""} still waiting to be activated. Confirm each vehicle below — no verification code needed.`,
    listHeading: "Pending vehicles",
    progressLabel: (current, total) => `Activating vehicle ${current} of ${total}`,
    submitLabel: "Activate this vehicle",
    submittingLabel: "Activating…",
    successToast: "Vehicle activated. Thank you!",
    errorToast: "Could not activate vehicle.",
    endpoint: "v1/customer/me/activate-vehicle",
  },
  capture: {
    title: "Link your memberships to a vehicle",
    description: (count) =>
      `You have ${count} membership${count !== 1 ? "s" : ""} that need to be linked to your car. Enter vehicle details for each one below.`,
    listHeading: "Vehicles to link",
    progressLabel: (current, total) => `Linking vehicle ${current} of ${total}`,
    submitLabel: "Save & link vehicle",
    submittingLabel: "Saving…",
    successToast: "Vehicle linked. Thank you!",
    errorToast: "Could not save vehicle details.",
    endpoint: "v1/customer/me/complete-vehicle-details",
  },
};

function isPlaceholderRego(rego) {
  const r = String(rego || "").trim().toUpperCase();
  if (!r) return true;
  if (r === PLACEHOLDER_REGO || r === "PENDING") return true;
  return /^PENDING-LINK-/i.test(r);
}

function displayRegoLabel(rego) {
  return isPlaceholderRego(rego) ? "No rego yet" : rego;
}

/**
 * Blocking portal flow for vehicles that still need member action:
 * - `activation`: partner signups — inactive vehicles, one-by-one, no OTP
 * - `capture`: self-signup — placeholder vehicles needing real rego/details
 */
const PendingVehicleActivationModal = ({ items, onResolved, mode = "activation" }) => {
  const config = MODE_CONFIG[mode] || MODE_CONFIG.activation;
  const pendingItems = items || [];
  const currentItem = pendingItems.length > 0 ? pendingItems[0] : null;
  const totalCount = pendingItems.length;
  const currentIndex = totalCount > 0 ? 1 : 0;

  const [rego, setRego] = useState("");
  const [vin, setVin] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [modelYear, setModelYear] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [completedIds, setCompletedIds] = useState([]);

  useEffect(() => {
    if (!currentItem) return;
    setRego(isPlaceholderRego(currentItem.rego) ? "" : currentItem.rego || "");
    setVin(
      currentItem.vin &&
        !String(currentItem.vin).startsWith("GEN-") &&
        currentItem.vin !== "PENDING" &&
        currentItem.vin !== "DEFAULTVINNUMBERS"
        ? currentItem.vin
        : ""
    );
    setMake(currentItem.make && currentItem.make !== "PENDING" ? currentItem.make : "");
    setModel(() => {
      const m = currentItem.model;
      if (m == null || m === "PENDING") return "";
      return typeof m === "string" ? m : m?.label || m?.value || "";
    });
    setModelYear(
      currentItem.modelYear && currentItem.modelYear !== "PENDING"
        ? String(currentItem.modelYear)
        : ""
    );
    setVehicleType(currentItem.vehicleType || "");
  }, [currentItem?.vehicleId]);

  const productSummary = useMemo(() => {
    if (!currentItem?.subscriptions?.length) return "";
    return currentItem.subscriptions.map((s) => s.productName).filter(Boolean).join(", ");
  }, [currentItem]);

  if (!currentItem) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const r = String(rego || "").trim();
    if (r.length < 2) {
      showErrorToast("Please enter your registration (number plate).");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api(config.endpoint, "POST", {
        vehicleId: currentItem.vehicleId,
        rego: r,
        ...(vin.trim() ? { vin: vin.trim() } : {}),
        ...(make.trim() ? { make: make.trim() } : {}),
        ...(model.trim() ? { model: model.trim() } : {}),
        ...(modelYear.trim() ? { modelYear: modelYear.trim() } : {}),
        ...(vehicleType && ["sedan", "wagon", "4wd"].includes(vehicleType) ? { vehicleType } : {}),
      });
      if (res.data?.success) {
        showSuccessToast(config.successToast);
        setCompletedIds((prev) => [...prev, String(currentItem.vehicleId)]);
        await onResolved({ mode });
      } else {
        showErrorToast(res.data?.message || config.errorToast);
      }
    } catch (err) {
      console.error(err);
      showErrorToast(`${config.errorToast} Try again or contact support.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[12000] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pending-vehicle-flow-title"
    >
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <h2 id="pending-vehicle-flow-title" className="text-xl font-semibold text-gray-900 mb-1">
          {config.title}
        </h2>
        <p className="text-sm text-gray-600 mb-4">{config.description(totalCount)}</p>

        <div className="mb-5 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            {config.listHeading}
          </p>
          <ul className="flex flex-col gap-2">
            {pendingItems.map((item) => {
              const isCurrent = String(item.vehicleId) === String(currentItem.vehicleId);
              const isDone = completedIds.includes(String(item.vehicleId));
              const label = displayRegoLabel(item.rego);
              const products =
                item.subscriptions?.map((s) => s.productName).filter(Boolean).join(", ") || "Membership";
              return (
                <li
                  key={item.vehicleId}
                  className={`flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm ${
                    isCurrent ? "bg-blue-100 border border-blue-300" : "bg-white border border-gray-200"
                  }`}
                >
                  <div className="min-w-0">
                    <span className="font-medium text-gray-900">{label}</span>
                    <span className="block text-xs text-gray-500 truncate">{products}</span>
                  </div>
                  <span
                    className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${
                      isDone
                        ? "bg-green-100 text-green-800"
                        : isCurrent
                          ? "bg-blue-600 text-white"
                          : "bg-amber-100 text-amber-800"
                    }`}
                  >
                    {isDone ? "Done" : isCurrent ? "Current" : "Pending"}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <p className="text-sm font-medium text-gray-800 mb-3">
          {config.progressLabel(currentIndex, totalCount)}
        </p>

        {productSummary && (
          <p className="text-sm text-gray-700 mb-4">
            Plan: <span className="font-normal">{productSummary}</span>
          </p>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Registration (number plate) *</label>
            <input
              type="text"
              value={rego}
              onChange={(e) => setRego(e.target.value)}
              className="border rounded px-3 py-2 w-full uppercase"
              placeholder="e.g. CX78FQ"
              autoComplete="off"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">VIN (optional)</label>
            <input
              type="text"
              value={vin}
              onChange={(e) => setVin(e.target.value)}
              className="border rounded px-3 py-2 w-full"
              autoComplete="off"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Make (optional)</label>
              <input
                type="text"
                value={make}
                onChange={(e) => setMake(e.target.value)}
                className="border rounded px-3 py-2 w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Model (optional)</label>
              <input
                type="text"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="border rounded px-3 py-2 w-full"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year (optional)</label>
              <input
                type="text"
                value={modelYear}
                onChange={(e) => setModelYear(e.target.value)}
                className="border rounded px-3 py-2 w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle type</label>
              <select
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value)}
                className="border rounded px-3 py-2 w-full"
              >
                <option value="">Select (optional)</option>
                <option value="sedan">Sedan / Hatch</option>
                <option value="wagon">Wagon / SUV</option>
                <option value="4wd">4WD / 7 Seater</option>
              </select>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="mt-2 bg-blue-600 text-white py-3 rounded-lg font-semibold disabled:opacity-50"
          >
            {submitting ? config.submittingLabel : config.submitLabel}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PendingVehicleActivationModal;
